import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { fetchFreePanorama } from './polyhavenService';

export default function PanoramaViewer() {
  const containerRef = useRef(null);
  const [prompt, setPrompt] = useState('South Pole');
  const [loading, setLoading] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');

  // Three.js 核心实例引用
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);

  // 1. 初始化 Three.js 场景
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // A. 创建场景
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // B. 创建相机（根据容器宽高比自适应 FOV）
    const aspect = container.clientWidth / container.clientHeight;
    const initialFov = aspect < 1 ? 90 : 75;

    const camera = new THREE.PerspectiveCamera(initialFov, aspect, 0.1, 1000);
    camera.position.set(0, 0, 0.1);
    cameraRef.current = camera;

    // C. 创建渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    
    // 阻止移动端手势默认滚动
    renderer.domElement.style.touchAction = 'none';

    container.innerHTML = '';
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // D. 反转内侧球体（全景幕布）
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    const material = new THREE.MeshBasicMaterial();
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);

    // E. 交互逻辑（拖拽与旋转）
    let isUserInteracting = false;
    let onPointerDownPointerX = 0, onPointerDownPointerY = 0;
    let lon = 0, onPointerDownLon = 0;
    let lat = 0, onPointerDownLat = 0;

    const onPointerDown = (e) => {
      isUserInteracting = true;
      onPointerDownPointerX = e.clientX;
      onPointerDownPointerY = e.clientY;
      onPointerDownLon = lon;
      onPointerDownLat = lat;
      container.style.cursor = 'grabbing';
    };

    const onPointerMove = (e) => {
      if (!isUserInteracting) return;
      lon = (onPointerDownPointerX - e.clientX) * 0.15 + onPointerDownLon;
      lat = (e.clientY - onPointerDownPointerY) * 0.15 + onPointerDownLat;
    };

    const onPointerUp = () => {
      isUserInteracting = false;
      container.style.cursor = 'grab';
    };

    // 滚轮缩放视角
    const onWheel = (e) => {
      e.preventDefault();
      if (!cameraRef.current) return;
      cameraRef.current.fov = Math.max(30, Math.min(100, cameraRef.current.fov + e.deltaY * 0.05));
      cameraRef.current.updateProjectionMatrix();
    };

    const dom = renderer.domElement;
    dom.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    dom.addEventListener('wheel', onWheel, { passive: false });

    // F. 渲染循环
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // 限制俯仰角在 -65° 到 65° 之间，避免南北极点处的严重拉伸
      lat = Math.max(-65, Math.min(65, lat));

      const phi = THREE.MathUtils.degToRad(90 - lat);
      const theta = THREE.MathUtils.degToRad(lon);

      const target = new THREE.Vector3(
        500 * Math.sin(phi) * Math.cos(theta),
        500 * Math.cos(phi),
        500 * Math.sin(phi) * Math.sin(theta)
      );

      camera.lookAt(target);
      renderer.render(scene, camera);
    };
    animate();

    // G. 窗口 Resize 监听
    const handleResize = () => {
      if (!container || !cameraRef.current || !rendererRef.current) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      const currentAspect = w / h;

      cameraRef.current.aspect = currentAspect;
      cameraRef.current.fov = currentAspect < 1 ? 90 : 75;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // H. 首次载入场景
    loadScene('South Pole');

    // 清理逻辑
    return () => {
      cancelAnimationFrame(animationFrameId);
      dom.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      dom.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', handleResize);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  // 2. 异步加载/切换全景贴图
  const loadScene = async (promptText) => {
    setLoading(true);
    try {
      const url = await fetchFreePanorama(promptText);
      setCurrentUrl(url);

      const loader = new THREE.TextureLoader();
      loader.crossOrigin = 'anonymous';
      
      loader.load(url, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        
        if (sceneRef.current && sceneRef.current.children[0]) {
          const sphere = sceneRef.current.children[0];
          if (sphere.material) {
            if (sphere.material.map) sphere.material.map.dispose(); // 释放旧内存
            sphere.material.map = texture;
            sphere.material.needsUpdate = true; // 触发材质重绘
          }
        }
        setLoading(false);
      });
    } catch (err) {
      console.error("加载全景贴图失败:", err);
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden', fontFamily: 'sans-serif' }}>
      
      {/* 3D 全景 WebGL 容器 */}
      <div ref={containerRef} style={{ width: '100%', height: '100%', cursor: 'grab' }} />

      {/* 顶部控制搜索栏 */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        padding: '10px 18px',
        borderRadius: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        zIndex: 10,
        maxWidth: '90%'
      }}>
        <input 
          type="text" 
          value={prompt} 
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="输入场景描述（如: South Pole, Louvre...）"
          style={{
            padding: '8px 12px',
            borderRadius: '16px',
            border: '1px solid #ccc',
            outline: 'none',
            width: '180px'
          }}
        />
        
        <button 
          onClick={() => loadScene(prompt)}
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: '#5D87FF',
            color: '#fff',
            fontWeight: '600',
            fontSize: '13px',
            cursor: loading ? 'wait' : 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {loading ? '检索中...' : '切换 360° 场景'}
        </button>
      </div>

      {/* 底部快捷预设按钮 */}
      <div style={{
        position: 'absolute',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '10px',
        zIndex: 10
      }}>
        {[
          { label: '🏛️ 卢浮宫展厅', query: 'Louvre' },
          { label: '❄️ 南极', query: 'South Pole' },
          { label: '🌲 森林公园', query: 'forest park' }
        ].map((item, index) => (
          <button
            key={index}
            onClick={() => {
              setPrompt(item.query);
              loadScene(item.query);
            }}
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: 'none',
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              color: '#fff',
              fontSize: '13px',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
              whiteSpace: 'nowrap'
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 底部贴图声明 */}
      {currentUrl && (
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '12px',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '11px',
          zIndex: 10
        }}>
          全景图全屏渲染
        </div>
      )}
    </div>
  );
}